/**
 * i18n Configuration for QuickMask
 * Supports English, Chinese (Simplified), and Spanish
 *
 * Single source of truth for UI locale (landing, auth, legal, app chrome). Persists via i18next localStorage.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en.json';
import zh from './locales/zh.json';
import es from './locales/es.json';

const LEGACY_LANDING_LANG_KEY = 'quickmask-landing-lang';

export type AppLocale = 'en' | 'es' | 'zh';

/** Map i18next language tags to app locale (en-US → en, zh-CN → zh). */
export function normalizeAppLocale(lng: string | undefined): AppLocale {
    const base = (lng || 'en').split('-')[0].toLowerCase();
    if (base === 'zh') return 'zh';
    if (base === 'es') return 'es';
    return 'en';
}

const HTML_LANG: Record<AppLocale, string> = {
    en: 'en',
    es: 'es',
    zh: 'zh-Hans',
};

function applyDocumentHtmlLang(lng: string) {
    document.documentElement.lang = HTML_LANG[normalizeAppLocale(lng)] ?? 'en';
}

/** One-time migration from landing-only storage to shared i18next locale. */
function migrateLegacyLandingLang() {
    try {
        const legacy = localStorage.getItem(LEGACY_LANDING_LANG_KEY);
        if (legacy === 'en' || legacy === 'es' || legacy === 'zh') {
            void i18n.changeLanguage(legacy);
            localStorage.removeItem(LEGACY_LANDING_LANG_KEY);
        }
    } catch {
        /* ignore */
    }
}

const resources = {
    en: { translation: en },
    zh: { translation: zh },
    es: { translation: es },
};

void i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources,
        fallbackLng: 'en',
        supportedLngs: ['en', 'zh', 'es'],
        nonExplicitSupportedLngs: true,
        debug: process.env.NODE_ENV === 'development',

        interpolation: {
            escapeValue: false, // React already escapes values
        },

        detection: {
            order: ['localStorage', 'navigator', 'htmlTag'],
            caches: ['localStorage'],
        },
    })
    .then(() => {
        migrateLegacyLandingLang();
        applyDocumentHtmlLang(i18n.language);
    });

i18n.on('languageChanged', (lng) => {
    applyDocumentHtmlLang(lng);
});

export default i18n;

// Available languages
export const languages = [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'zh', name: 'Chinese', nativeName: '中文' },
    { code: 'es', name: 'Spanish', nativeName: 'Español' },
];

// Get current language
export const getCurrentLanguage = () => i18n.language;

// Change language
export const changeLanguage = (lang: string) => i18n.changeLanguage(lang);
